import moment from 'moment-timezone';
import { useCallback, useId, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useSearchParams } from 'react-router-dom';
import { BudgetSpend, ContextualMenu, showNotification, Tag } from 'sonata-react-components';

import { PromptModal } from '@/components/common/PromptModal';
import { useModals } from '@/router';
import { deleteAdvertiser, getAdvertisers } from '@/services/advertiser.service';
import { useGlobalStore } from '@/store/global';
import { getUser } from '@/store/global/user/selectors.ts';
import { CampaignsStore, useStore as useCampaignStore } from '@/store/local/campaigns';
import { EntityNameTooltip } from '../Shared/EntityNameTooltip';
import { type EntityRowAction, EntityRowActions } from '../Shared/EntityTable/EntityRowActions';
import { EntityTable, type EntityTableColumn } from '../Shared/EntityTable/EntityTable';
import { useAdvertiserColumnLabels } from './advertiserColumns';
import './advertiserList.scss';

import { getEntityDiagnostic } from 'sonata-static-storage/lib/entities';

import { getBaseCurrency } from '@/store/global/baseCurrency/selectors';

import type { AdvertiserWithExtraData } from 'sonata-static-storage/lib/common/common.types';

type AdvertiserAction = EntityRowAction;

type TaptapListItem = {
  actions: AdvertiserAction[];
  activeCampaignPlans: number;
  advertiser: AdvertiserWithExtraData;
  agency: string;
  creationDate: string;
  id: string;
  mediaGroup: string;
  name: string;
  titleOnClick: () => void;
};

type Props = {
  cacheSeed: string;
  isColumnVisible: (columnId: string) => boolean;
  filters: Record<string, string | number | (string | number)[]>;
  onListChange: () => void;
};

type State = {
  advertiserToDelete?: AdvertiserWithExtraData;
  showDeletionPrompt: boolean;
};

export function AdvertiserList({ cacheSeed, filters, isColumnVisible: isVisible, onListChange }: Readonly<Props>) {
  const { formatMessage } = useIntl();
  const columnLabels = useAdvertiserColumnLabels();
  const modals = useModals();
  const listId = useId();
  const [, setSearchParams] = useSearchParams();

  const { id: userId = 'N/A' } = useGlobalStore(getUser).entity;
  const baseCurrency = useGlobalStore(getBaseCurrency);
  const { setHasAdvertisers } = useCampaignStore() as CampaignsStore;

  const modalsRef = useRef(modals);
  const onListChangeRef = useRef(onListChange);
  modalsRef.current = modals;
  onListChangeRef.current = onListChange;

  const [{ advertiserToDelete, showDeletionPrompt }, setState] = useState<State>({
    advertiserToDelete: undefined,
    showDeletionPrompt: false
  });

  const handleConfirmDeletion = async () => {
    if (!advertiserToDelete) {
      return;
    }
    try {
      await deleteAdvertiser(advertiserToDelete._id, userId);
      const { totalItems } = await getAdvertisers(userId, new URLSearchParams({ itemsPerPage: '1' }), {});
      if (totalItems === 0) {
        setHasAdvertisers(false);
      }
      onListChange();
      setState(prevState => ({
        ...prevState,
        advertiserToDelete: undefined,
        showDeletionPrompt: false
      }));
      showNotification(
        'success',
        formatMessage({
          defaultMessage: 'Advertiser deleted successfully',
          id: 'advertisers.notifications.deleted'
        })
      );
    } catch {
      showNotification(
        'error',
        formatMessage({
          defaultMessage: 'Failure deleting the Advertiser',
          id: 'advertisers.notifications.notDeleted'
        })
      );
    }
  };

  const handleCancelDeletion = () => {
    setState(prevState => ({
      ...prevState,
      advertiserToDelete: undefined,
      showDeletionPrompt: false
    }));
  };

  const handleTitleClick = (advertiserId: string) => {
    setSearchParams((prevParams: URLSearchParams) => {
      prevParams.set('tab', 'campaignPlan');
      prevParams.set('advertiserId', advertiserId);
      return prevParams;
    });
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: cacheSeed is intentionally listed to trigger re-fetches on external invalidation
  const fetchData = useCallback(
    async (params: { page: number; pageSize: number }) => {
      const filterParams = Object.fromEntries(
        Object.entries(filters).map(([k, v]) => [k, Array.isArray(v) ? v.join(',') : String(v)])
      );
      const searchParams = new URLSearchParams({
        includeExtraData: 'true',
        itemsPerPage: params.pageSize.toString(),
        page: params.page.toString(),
        sortBy: 'modified',
        sortOrder: 'desc',
        ...filterParams
      });

      try {
        const response = await getAdvertisers(userId, searchParams, {});

        const items: TaptapListItem[] =
          response?.items?.map(item => {
            const advertiser = item as AdvertiserWithExtraData;
            const _id = advertiser._id ?? '';
            const typedAdvertiser = advertiser as AdvertiserWithExtraData & {
              created?: string;
              mediaAgency?: { name: string };
              mediaGroup?: { name: string };
              modified?: string;
            };

            return {
              actions: [
                {
                  icon: 'fa-solid fa-pen-to-square',
                  iconClassName: '',
                  id: `advertiser-edit-${_id}`,
                  onClick: () =>
                    modalsRef.current.open('/campaigns/new/advertiserModal', {
                      at: { pathname: '/campaigns', search: location.search },
                      state: { advertiser, modalId: 'advertiser-modal' }
                    })
                },
                {
                  content: (
                    <ContextualMenu
                      buttons={[
                        {
                          icon: 'fal fa-plus',
                          id: `advertiser-action-createCp-${_id}`,
                          onClick: () => {
                            onListChangeRef.current();
                            modalsRef.current.open('/campaigns/new/campaignPlanSettingsModal', {
                              at: { pathname: '/campaigns', search: location.search },
                              state: { advertiser, modalId: 'campaignPlanSettings-modal' }
                            });
                          },
                          text: 'Create Campaign Plan'
                        },
                        {
                          disabled: advertiser.campaignPlanIds.length > 0,
                          icon: 'fa fa-trash-alt',
                          id: `advertiser-action-delete-${_id}`,
                          onClick: () =>
                            setState(prevState => ({
                              ...prevState,
                              advertiserToDelete: advertiser,
                              showDeletionPrompt: true
                            })),
                          text: 'Delete'
                        }
                      ]}
                      id={`advertiser-contextual-menu-${_id}`}
                    />
                  ),
                  id: `advertiser-more-actions-${_id}`,
                  isCustomAction: true
                }
              ],
              activeCampaignPlans: advertiser.activeCampaignPlanIds.length,
              advertiser,
              agency: typedAdvertiser.mediaAgency?.name ?? 'N/A',
              creationDate: moment.utc(typedAdvertiser.modified || typedAdvertiser.created).format('DD/MM/YYYY'),
              id: _id,
              mediaGroup: typedAdvertiser.mediaGroup?.name ?? 'N/A',
              name: advertiser.name ?? 'N/A',
              titleOnClick: () => handleTitleClick(_id)
            };
          }) ?? [];

        return {
          data: items,
          page: response.page as number,
          pageSize: params.pageSize,
          total: response.totalItems,
          totalPages: Math.ceil(response.totalItems / params.pageSize)
        };
      } catch {
        showNotification(
          'error',
          formatMessage({
            defaultMessage: 'Failed to load advertisers',
            id: 'advertisers.fetchData.error'
          })
        );
        return { data: [], page: params.page, pageSize: params.pageSize, total: 0, totalPages: 0 };
      }
    },
    [userId, filters, cacheSeed]
  );

  const columns: EntityTableColumn<TaptapListItem>[] = [
    {
      header: columnLabels.agency,
      id: 'agency',
      render: item => (
        <span className="multiflightAgency" title={item.agency}>
          {item.agency}
        </span>
      ),
      width: 'var(--advertiserList-col-agency)'
    },
    {
      header: columnLabels.mediaGroup,
      id: 'mediaGroup',
      render: item => (
        <span className="multiflightMediaGroup" title={item.mediaGroup}>
          {item.mediaGroup}
        </span>
      ),
      width: 'var(--advertiserList-col-media-group)'
    },
    {
      header: columnLabels.campaignActive,
      id: 'campaignActive',
      render: item => (
        <span className="multiflightCampaignActive">
          <Tag id={`status-${item.id}`} status={item.activeCampaignPlans === 0 ? 'warning' : 'enabled'}>
            {item.activeCampaignPlans}
          </Tag>
        </span>
      ),
      width: 'var(--advertiserList-col-campaign-active)'
    },
    {
      header: columnLabels.creationDate,
      id: 'creationDate',
      render: item => <span className="multiflightLastUpdate">{item.creationDate}</span>,
      width: 'var(--advertiserList-col-creation)'
    },
    {
      align: 'right',
      header: columnLabels.budgetSpend,
      id: 'budgetSpend',
      render: item => (
        <span className="multiflightBudgetSpend">
          <BudgetSpend
            currencyInfo={baseCurrency}
            entityId={item.id}
            getBudget={() => getEntityDiagnostic(userId, item.id, 'advertiser')}
          />
        </span>
      ),
      width: 'var(--advertiserList-col-budget)'
    }
  ];
  const visibleColumns = columns.filter(column => isVisible(column.id));

  return (
    <main className="advertiserList">
      {showDeletionPrompt && (
        <PromptModal
          cancelCallback={handleCancelDeletion}
          confirmCallback={handleConfirmDeletion}
          texts={{
            buttons: {
              cancel: formatMessage({
                defaultMessage: 'Cancel',
                id: 'common.cancel'
              }),
              confirm: formatMessage({
                defaultMessage: 'Confirm',
                id: 'common.confirm'
              })
            },
            description: formatMessage(
              {
                defaultMessage:
                  "Deleting the advertiser {advertiser} can't be undone and all associated information will be permanently lost.",
                id: 'advertisers.prompts.deletion.description'
              },
              {
                advertiser: advertiserToDelete?.name
              }
            ),
            title: formatMessage({
              defaultMessage: 'Are you sure you want to delete the selected advertiser?',
              id: 'advertisers.prompts.deletion.title'
            })
          }}
          variant="warning"
        />
      )}
      <EntityTable<TaptapListItem>
        actionsWidth="var(--advertiserList-col-actions)"
        className="advertiserList"
        columns={visibleColumns}
        emptyStateTitle={formatMessage({
          defaultMessage: 'There is no data for the current search.',
          id: 'advertisers.list.emptyState.searchTitle'
        })}
        fetchData={fetchData}
        id={listId}
        initialPageSize={50}
        nameColumn={{
          header: formatMessage({ defaultMessage: 'Advertiser', id: 'advertisers.list.toolbar.advertiser' }),
          render: item => (
            <div className="multiflightTitle">
              <EntityNameTooltip name={item.name}>
                <button
                  className="advertiserList__titleButton"
                  data-row-action
                  onClick={item.titleOnClick}
                  type="button"
                >
                  {item.name}
                </button>
              </EntityNameTooltip>
            </div>
          )
        }}
        renderActions={item => <EntityRowActions actions={item.actions} className="advertiserList" />}
      />
    </main>
  );
}
